import { Injectable } from '@angular/core';
import { from, Observable, of } from 'rxjs';
import { settings } from '../../settings';
import { Agreement } from '../../shared/models/agreement.model';
import { Consumer } from '../../shared/models/consumer.model';
import { JobFilter } from '../../shared/models/job-filter.model';
import { Job } from '../../shared/models/job.model';
import { Remote } from '../remote';
import { BaseService } from '../services/base.service';

/**
 * Provides an interface for remote services.
 */
@Injectable({
  providedIn: 'root',
})
export class ServicesProvider extends Remote {
  public agreements = {
    acceptOffer: (service: BaseService, agreement: Agreement) => this.performAgreementAction(service, 'accept_offer', agreement),
    declineOffer: (service: BaseService, agreement: Agreement) => this.performAgreementAction(service, 'decline_offer', agreement),
    acceptAmendments: (service: BaseService, agreement: Agreement) => this.performAgreementAction(service, 'accept_amendments', agreement),
    declineAmendments: (service: BaseService, agreement: Agreement) => this.performAgreementAction(service, 'decline_amendments', agreement),
    declineRequest: (service: BaseService, agreement: Agreement) => {
      const url = this.constructUrl(service, 'decline_request', agreement);

      return this.apiCallRetry('POST', url);
    },
    canCreateAgreement: (service: BaseService, consumerId: number | string) => {
      const url = this.constructUrl(service, `can_create/${consumerId}`);
      return this.apiCallRetry('GET', url);
    },

    // Get calculated rate for worker
    getRates: (rate: number | string) => {
      const url = `${this.api}/api/v1/workers/agreements/rate?base_rate=${rate}`;
      return this.apiCallRetry('GET', url).then((res) => res.body.data.attributes);
    },
  };

  public consumer = {
    openJobs: (service: BaseService, consumer: Consumer) => {
      // GET /workers/consumers/:consumer_id/private_and_public_jobs
      const url = this.constructUrl(service, 'private_and_public_jobs', consumer);

      return this.apiCallRetry('GET', url)
        .then((res) => res.body.data.map((elem) => elem.attributes)) as Promise<ReadonlyArray<Job>>;
    },
  };

  public invitations = {
    acceptInvite: (service: BaseService, inviteId: number | string) => {
      const url = this.constructUrl(service, `${inviteId}/accept`);
      return this.apiCallRetry('GET', url);
    },
    declineInvite: (service: BaseService, inviteId: number | string) => {
      const url = this.constructUrl(service, `${inviteId}/decline`);
      return this.apiCallRetry('GET', url);
    },
  };

  public meetAndGreet = {
    acceptMeetAndGreet: (id: number | string) => {
      const url = `${this.api}/api/v1/meet_and_greets/${id}/accept`;

      return this.apiCallRetry('PUT', url, undefined, null);
    },
    declineMeetAndGreet: (id: number | string) => {
      const url = `${this.api}/api/v1/meet_and_greets/${id}/decline`;

      return this.apiCallRetry('PUT', url, undefined, null);
    },
    create: (data) => {
      const url = `${this.api}/api/v1/meet_and_greets`;

      return this.apiCallRetry('POST', url, undefined, data);
    },
    cancel: (id: number | string) => {
      const url = `${this.api}/api/v1/meet_and_greets/${id}/cancel`;

      return this.apiCallRetry('PUT', url, undefined, null);
    },
  };

  public jobFilters = {
    filterKey: (service: BaseService, jobFilter: JobFilter) => {
      const url = this.constructUrl(service, 'get_key');

      return this.apiCallRetry('POST', url, undefined, jobFilter).then(
        (res) => res.body,
      ) as Promise<any>;
    },
  };

  // Inherited from Remote:
  // read<T>(service: BaseService, id: number): Promise<T>

  // Registers the device token on rails backend
  registerDevice(token: string, deviceAppInfo: any): Promise<any> {
    const local = this.isLocalHost ? '/api' : '';
    const url = `${this.api + local}/ionic_authentication/register_push_tokens`;
    const data = { token, device: deviceAppInfo };

    return this.apiCallRetry('POST', url, undefined, data);
  }

  /**
   * To get base object
   *
   * e.g. base: /dashboard, /profile
   *
   * @param service
   * @param version - Its to get a specific version.
   * e.g /dashboard/visit
   */
  public get(service: BaseService, version?: any): Promise<any> {
    const url = this.constructUrl(service, version);

    return this.apiCallRetry('GET', url, service.getParams())
      .then((res) => res.body.data.attributes);
  }

  public restore<T>(service: BaseService, item: T): Promise<any> {
    // PATCH [/acct_scope]/service/:item_id/restore
    const url = this.constructUrl(service, 'restore', item);

    return this.apiCallRetry('PATCH', url, undefined, item);
  }

  public contact(id: number) {
    const url = `${this.api}/profile/new_contact/${id}`;

    return this.apiCallRetry('GET', url)
      .then((res) => res.body);
  }

  public verify(code: string) {
    const url = `${this.api}/sms_verification/create`;
    const params = { verification_code: code };

    return this.apiCallRetry('GET', url, params)
      .then((res) => res.body);
  }

  public resetPassword(email: string, resetOption: string) {
    const url = resetOption === 'SMS' ? `${this.api}/users/password_reset` : `${this.api}/users/password`;
    const data = { user: { email } };

    return this.apiCallRetry('POST', url, undefined, data).then((res) => res.body);
  }

  public sendCode(): Promise<any> {
    const url = `${this.api}/sms_verification/new`;

    return this.apiCallRetry('GET', url).then((res) => res.body).catch((res) => {
      if (res && res.error && res.error.length) {
        return Promise.reject(res.error.errors[0].detail.join(', '));
      }
      return Promise.reject(`Unable to perform action. Please try again later. If problem persists please contact ${settings.brand.name}`);
    });
  }

  public update_account(user: any) {
    const url = `${this.api}/settings/account`;
    return this.apiCallRetry('PATCH', url, undefined, { user })
      .then((res) => res.body);
  }

  public user_account() {
    const url = `${this.api}/settings/account`;
    return this.apiCallRetry('GET', url)
      .then((res) => res.body);
  }

  public userMessageTemplate() {
    const url = `${this.api}/message_templates/by_name/COVID_19_PROTOCOL_TEMPLATE`;
    return this.apiCallRetry('GET', url)
      .then((res) => res.body);
  }

  /** Private utility members */

  protected perform<T>(service: BaseService, action: string, item: T, data?: any): Promise<T> {
    const url = this.constructUrl(service, action, item);

    // Attributes can be sent as params for partial update
    return this.apiCallRetry('PATCH', url, undefined, data)
      .then((res) => res.body.data.attributes) as Promise<T>;
  }

  private performAgreementAction(service: BaseService, action: string, item: Agreement): Promise<Agreement> {
    const url = this.constructUrl(service, action, item);

    return this.apiCallRetry('POST', url)
      .then((res) => res.body.data.attributes);
  }

  protected constructUrl(service: BaseService, action: string, item?: any): string {
    const itemStr = item ? `/${item.id}` : '';
    const actionStr = action ? `/${action}` : '';

    return `${this.api}/${this.endpointFor(service)}${itemStr}${actionStr}`;
  }

  public postGreetings(greetings) {
    const url = `${this.api}/greetings`;

    return this.apiCallRetry('POST', url, undefined, greetings).then(
      (res) => res.body,
    );
  }

  public getMableDigitalId(id: number): Promise<any> {
    const url = `${this.api}/profiles/search_digital/${id}`;

    return this.apiCallRetry('GET', url).then(
      (res) => res.body.data,
    );
  }

  /**
   * Its calling the API passed by backend in content (dashboard).
   *
   * @param link - url passed by backend
   */
  public async backendGetCall(link: string) {
    const url = `${this.api}${link}`;
    const response = await this.apiCallRetry('GET', url);
    return response.status === 200;
  }

  /**
   * TODO- Move Block/Unblock to API V1 and extract to service
   *
   * @param id
   */
  public async getBlockUserReasons(id: number) {
    const url = `${this.api}/api/v1/incidents/users/${id}/blocks/new`;
    const response = await this.apiCallRetry('GET', url);
    return response.body.data.attributes.reason_options;
  }

  public async blockUser(id: number, data): Promise<any> {
    const url = `${this.api}/api/v1/incidents/users/${id}/blocks`;
    const response = await this.apiCallRetry('POST', url, undefined, data);
    return response.status === 200;
  }

  public async getUnblockUserReasons(id: number): Promise<any> {
    const url = `${this.api}/api/v1/incidents/users/${id}/unblocks/new`;
    const response = await this.apiCallRetry('GET', url);
    return response.body.data.attributes.reason_options;
  }

  public async unblockUser(id: number, data): Promise<any> {
    const url = `${this.api}/api/v1/incidents/users/${id}/unblocks`;
    const response = await this.apiCallRetry('POST', url, undefined, data);
    return response.status === 200;
  }

  /**
   * Sends a GET request to the API for checking password strength.
   *
   * @param password - Entered password.
   */
  checkPasswordStrength(password) {
    try {
      const url = `${this.api}/api/v1/user/password_strength_check`;
      return from(this.apiCallRetry('POST', url, undefined, { password })
        .then(
          (res) => (res ? res.body.data.attributes : {}),
          () => ({}),
        ));
    } catch (e) {
      console.log(e.error);
      return null;
    }
  }

  public async getAwsCredentials(): Promise<any> {
    try {
      const url = `${this.api}/api/v1/aws_credentials`;
      const res = await this.apiCallRetry('GET', url);
      return res ? res.body : {};
    } catch (e) {
      return null;
    }
  }

  /**
   * Get list of services base on worker_type/care type
   *
   * @param type
   */
  public async getServicesList(type): Promise<any> {
    const url = `${this.api}/api/v1/services?filter[category]=${type}`;
    const response = await this.apiCallRetry('GET', url);
    return response.body.data;
  }

  getTimesheetCount(): Observable<any> {
    try {
      const url = `${this.api}/api/v1/event_deliveries/count`;
      return from(this.apiCallRetry('GET', url)
        .then(
          (res) => (res.body),
          () => ({}),
        ));
    } catch (e) {
      return of(e);
    }
  }
}
